import { action, computed, makeObservable, observable } from "mobx";
import moment from "moment";
import type { DateRange } from "react-day-picker";

import {
  coerceCompletionValue,
  formatFilterValueForDisplay,
  getOperatorsForFilter,
  matchFilterDefinitions,
  matchOperatorsForFilter,
  shouldSuppressValueDropdown as shouldSuppressValueDropdownForSchema,
} from "../../config/schema-utils";
import { isNestedFilterType } from "../../config/filter-value-type-behavior";
import {
  FilterValueType,
  getFilterDefinitionByKey,
  getPromptFilterDefinition,
  type FilterDefinition,
  type StructuredSearchSchema,
} from "../../config/structured-search-schema";
import {
  getVersionedEntityConfig,
  isVersionedEntityField,
} from "../../config/versioned-entity-config";
import {
  ANY_VERSION_LABEL,
  createVersionedEntityFilterValue,
  formatVersionedEntityDisplayLabel,
  getVersionedEntitySelectionKey,
  isVersionedEntityFilterValue,
  isVersionedEntityFilterValueList,
} from "../../config/versioned-entity-utils";
import {
  type Filter,
  type FilterValue,
  type PromptFilterValue,
  type VersionedEntityFilterValue,
} from "../../config/types";

const PROMPT_FILTER_OPERATOR = "is";
const PROMPT_ANY_VERSION_LABEL = ANY_VERSION_LABEL;
const LOCKED_FILTER_ID_PREFIX = "locked:";
export const PLAIN_SEARCH_CHIP_ID = "plain-search" as const;

const isMultiSelectOperator = (operator: string | undefined): boolean =>
  operator === "in" || operator === "not_in";

export interface CompleteDateRange {
  from: Date;
  to: Date;
}

export const isCompleteDateRange = (value: DateRange | undefined): value is CompleteDateRange =>
  !!value?.from &&
  !!value?.to &&
  !Number.isNaN(value.from.getTime()) &&
  !Number.isNaN(value.to.getTime());

const cloneCompleteDateRange = (value: CompleteDateRange): CompleteDateRange => ({
  from: new Date(value.from),
  to: new Date(value.to),
});

export const createStructuredSearchDefaultDateRange = (now = new Date()): CompleteDateRange => {
  const currentMoment = moment(now);
  return {
    from: currentMoment.clone().subtract(13, "days").startOf("day").toDate(),
    to: currentMoment.clone().endOf("day").toDate(),
  };
};

const normalizeStructuredSearchDateRange = (
  value: DateRange | undefined,
  fallback: CompleteDateRange = createStructuredSearchDefaultDateRange(),
): CompleteDateRange =>
  isCompleteDateRange(value) ? cloneCompleteDateRange(value) : cloneCompleteDateRange(fallback);

export type BuilderStep = "idle" | "field" | "key" | "operator" | "value";

export interface StructuredSearchSorting {
  id: string;
  desc: boolean;
}

interface InProgressToken {
  field?: string;
  type?: FilterValueType;
  operator?: string;
  value?: FilterValue;
  valueDisplay?: string;
  nestedKey?: string;
  nestedKeyDisplay?: string;
  promptId?: number;
  promptName?: string;
  entityId?: number;
  entityName?: string;
  multiValues?: string[];
  multiValueDisplay?: string[];
  promptMultiValues?: PromptFilterValue[];
  entityMultiValues?: VersionedEntityFilterValue[];
}

interface ToggleMultiValueFilterOptions {
  field: string;
  type: FilterValueType;
  value: string;
  valueDisplay?: string;
}

interface SetMultiValueFilterOptions {
  field: string;
  type: FilterValueType;
  values: {
    value: string;
    valueDisplay?: string;
  }[];
}

enum VersionedEntityStepMode {
  None = "none",
  SelectEntity = "select-entity",
  SelectVersion = "select-version",
}

const createFieldSelectionState = (field: string, type: FilterValueType): InProgressToken => ({
  field,
  type,
});

const parseCommaSeparatedValues = (value: string): string[] =>
  value
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);

const normalizeMultiValueEntries = (values: string[]): string[] =>
  values.flatMap(value => parseCommaSeparatedValues(value));

const getNormalizedMultiValues = (value: FilterValue): string[] => {
  if (Array.isArray(value)) {
    return normalizeMultiValueEntries(value.map(item => String(item).trim()).filter(Boolean));
  }
  if (typeof value === "string") {
    return parseCommaSeparatedValues(value);
  }
  return [];
};

const getStringFilterValues = (filter: Filter): string[] => {
  if (Array.isArray(filter.value)) {
    return filter.value.map(value => String(value).trim()).filter(Boolean);
  }

  if (typeof filter.value === "string") {
    return filter.value.trim() ? [filter.value.trim()] : [];
  }

  return [];
};

const getDateTimestamp = (value: Date | undefined): number | undefined => {
  if (!value) {
    return undefined;
  }

  const timestamp = value.getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

const areDateRangesEqual = (left: DateRange | undefined, right: DateRange | undefined): boolean =>
  getDateTimestamp(left?.from) === getDateTimestamp(right?.from) &&
  getDateTimestamp(left?.to) === getDateTimestamp(right?.to);

const getFilterIdentity = (filter: Filter): string =>
  JSON.stringify({
    field: filter.field,
    type: filter.type,
    operator: filter.operator,
    value: filter.value,
    valueDisplay: filter.valueDisplay,
    multiValueDisplay: filter.multiValueDisplay,
    nestedKey: filter.nestedKey,
    nestedKeyDisplay: filter.nestedKeyDisplay,
  });

const isPromptFilterValue = (value: unknown): value is PromptFilterValue =>
  typeof value === "object" &&
  value !== null &&
  "prompt_id" in value &&
  typeof value.prompt_id === "number" &&
  (!("prompt_version_number" in value) ||
    value.prompt_version_number === undefined ||
    typeof value.prompt_version_number === "number");

const isPromptFilterValueList = (value: FilterValue): value is PromptFilterValue[] =>
  Array.isArray(value) && value.every(item => isPromptFilterValue(item));

const isPromptNameValue = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const promptFilterValueMatchesPrompt = (value: FilterValue, promptId: number): boolean => {
  if (isPromptFilterValue(value)) {
    return value.prompt_id === promptId;
  }

  return (
    Array.isArray(value) &&
    value.some(item => isPromptFilterValue(item) && item.prompt_id === promptId)
  );
};

const getPromptSelectionKey = (value: PromptFilterValue): string =>
  `${value.prompt_id}:${value.prompt_version_number ?? "any"}`;

const parseNumericBetweenValue = (rawValue: FilterValue | undefined): [number, number] | null => {
  if (Array.isArray(rawValue) && rawValue.length >= 2) {
    const left = Number(rawValue[0]);
    const right = Number(rawValue[1]);
    if (Number.isFinite(left) && Number.isFinite(right)) {
      return [left, right];
    }
    return null;
  }

  if (typeof rawValue !== "string") {
    return null;
  }

  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }

  let parsedParts: unknown[] | null = null;
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        parsedParts = parsed;
      }
    } catch {
      return null;
    }
  } else if (trimmed.includes(",")) {
    parsedParts = trimmed.split(",").map(part => part.trim());
  }

  if (!parsedParts || parsedParts.length < 2) {
    return null;
  }

  const left = Number(parsedParts[0]);
  const right = Number(parsedParts[1]);
  if (!Number.isFinite(left) || !Number.isFinite(right)) {
    return null;
  }

  return [left, right];
};

export class StructuredSearchStore {
  @observable private _inputText = "";
  @observable private _step: BuilderStep = "idle";
  @observable private _inProgress: InProgressToken = {};
  @observable.shallow private _filters: Filter[] = [];
  @observable private _keyboardIndex = 0;
  @observable private _dropdownOpen = false;
  @observable private _editingFilterId: string | null = null;
  @observable private _selectedFilterIdForRemoval: string | null = null;
  @observable private _applyValidationMessage: string | null = null;
  @observable.shallow private _lockedFilters: Filter[] = [];
  @observable private _dateRange: CompleteDateRange;
  @observable private _q = "";
  @observable private _pageIndex = 1;
  @observable private _pageSize = 25;
  @observable.shallow private _sorting: StructuredSearchSorting[] = [];
  @observable private _schema: StructuredSearchSchema;
  @observable private _urlHydrationReady = false;
  private readonly _createDefaultDateRange: () => CompleteDateRange;
  private _schemaStateById = new Map<
    string,
    {
      filters: Filter[];
      q: string;
      dateRange: CompleteDateRange;
      sorting: StructuredSearchSorting[];
      pageIndex: number;
      pageSize: number;
    }
  >();

  constructor(
    schema: StructuredSearchSchema,
    createDefaultDateRange: () => CompleteDateRange = createStructuredSearchDefaultDateRange,
    initialLockedFilters: Filter[] = [],
  ) {
    this._schema = schema;
    this._createDefaultDateRange = createDefaultDateRange;
    this._dateRange = cloneCompleteDateRange(this._createDefaultDateRange());
    makeObservable(this);
    this.setLockedFilters(initialLockedFilters);
  }

  get schema(): StructuredSearchSchema {
    return this._schema;
  }

  private isPromptField(field: string | undefined): boolean {
    const promptFilter = getPromptFilterDefinition(this._schema);
    return promptFilter !== undefined && field === promptFilter.key;
  }

  private isTraceVersionedEntityField(field: string | undefined): boolean {
    return isVersionedEntityField(this._schema, field) && !this.isPromptField(field);
  }

  private getInProgressEntityId(): number | undefined {
    if (this.isPromptField(this._inProgress.field)) {
      return this._inProgress.promptId;
    }

    return this._inProgress.entityId;
  }

  private getInProgressEntityName(): string {
    if (this.isPromptField(this._inProgress.field)) {
      return this._inProgress.promptName ?? "";
    }

    return this._inProgress.entityName ?? "";
  }

  private isNestedField(field: string | undefined): boolean {
    const definition = getFilterDefinitionByKey(this._schema, field);
    return definition ? isNestedFilterType(definition.type) : false;
  }

  private shouldSuppressValueDropdown(field: string | undefined, operator?: string): boolean {
    return shouldSuppressValueDropdownForSchema(this._schema, field, operator);
  }

  private createOperatorSelectionState(
    current: InProgressToken,
    operator: string,
  ): InProgressToken {
    const nextState: InProgressToken = {
      field: current.field,
      type: current.type,
      operator,
    };

    if (this.isNestedField(current.field) && current.nestedKey) {
      nextState.nestedKey = current.nestedKey;
      nextState.nestedKeyDisplay = current.nestedKeyDisplay;
    }

    if (isMultiSelectOperator(operator)) {
      nextState.multiValues = [];
      nextState.multiValueDisplay = [];
    }

    return nextState;
  }

  private createBacktrackedOperatorSelectionState(current: InProgressToken): InProgressToken {
    const nextState: InProgressToken = {
      field: current.field,
      type: current.type,
      operator: current.operator,
    };

    if (this.isNestedField(current.field) && current.nestedKey) {
      nextState.nestedKey = current.nestedKey;
      nextState.nestedKeyDisplay = current.nestedKeyDisplay;
    }

    return nextState;
  }

  private createBacktrackedKeySelectionState(current: InProgressToken): InProgressToken {
    return {
      field: current.field,
      type: current.type,
    };
  }

  private getVersionedEntityStepMode(): VersionedEntityStepMode {
    if (this._step !== "value") {
      return VersionedEntityStepMode.None;
    }

    if (!isVersionedEntityField(this._schema, this._inProgress.field)) {
      return VersionedEntityStepMode.None;
    }

    return this.getInProgressEntityId() === undefined
      ? VersionedEntityStepMode.SelectEntity
      : VersionedEntityStepMode.SelectVersion;
  }

  private getInitialEditInputText(
    filter: Filter,
    needsValue: boolean,
    operatorDisplay: string | undefined,
  ): string {
    if (needsValue && !isMultiSelectOperator(filter.operator)) {
      return (
        filter.valueDisplay ??
        formatFilterValueForDisplay(this._schema, filter.value ?? "", filter.field)
      );
    }

    if (isMultiSelectOperator(filter.operator)) {
      return "";
    }

    return operatorDisplay ?? "";
  }

  get inputText() {
    return this._inputText;
  }

  get step() {
    return this._step;
  }

  get inProgress() {
    return this._inProgress;
  }

  get filters() {
    return this._filters;
  }

  get lockedFilters() {
    return this._lockedFilters;
  }

  /** Editable filters only — locked filters are route/context-derived and must not enter the URL. */
  get editableFilters() {
    return this._filters.filter(filter => !this.isFilterLocked(filter.id));
  }

  get keyboardIndex() {
    return this._keyboardIndex;
  }

  get dropdownOpen() {
    return this._dropdownOpen;
  }

  get editingFilterId() {
    return this._editingFilterId;
  }

  get selectedFilterIdForRemoval() {
    return this._selectedFilterIdForRemoval;
  }

  get applyValidationMessage() {
    return this._applyValidationMessage;
  }

  get dateRange(): CompleteDateRange {
    return this._dateRange;
  }

  get q() {
    return this._q;
  }

  get pageIndex() {
    return this._pageIndex;
  }

  get pageSize() {
    return this._pageSize;
  }

  get sorting() {
    return this._sorting;
  }

  get urlHydrationReady() {
    return this._urlHydrationReady;
  }

  @action setUrlHydrationReady(ready: boolean) {
    this._urlHydrationReady = ready;
  }

  @computed get hasFilters() {
    return this._filters.length > 0;
  }

  @computed get hasLockedPromptFilter() {
    return this._lockedFilters.some(filter => this.isPromptField(filter.field));
  }

  @computed get fieldSuggestions(): FilterDefinition[] {
    return matchFilterDefinitions(this._schema, this._inputText);
  }

  @computed get isVersionedEntityVersionStep(): boolean {
    return this.getVersionedEntityStepMode() === VersionedEntityStepMode.SelectVersion;
  }

  @computed get isVersionedEntitySelectStep(): boolean {
    return this.getVersionedEntityStepMode() === VersionedEntityStepMode.SelectEntity;
  }

  @computed get isPromptVersionStep(): boolean {
    return this.isVersionedEntityVersionStep;
  }

  @computed get isPromptSelectStep(): boolean {
    return this.isVersionedEntitySelectStep;
  }

  @computed get isMultiSelectMode(): boolean {
    if (this._step !== "value" || !isMultiSelectOperator(this._inProgress.operator)) {
      return false;
    }

    if (
      this.isPromptField(this._inProgress.field) ||
      this.isTraceVersionedEntityField(this._inProgress.field)
    ) {
      return true;
    }

    if (this.isNestedField(this._inProgress.field) && !this._inProgress.nestedKey) {
      return false;
    }

    return true;
  }

  @computed get multiValues(): string[] {
    if (
      this.isPromptField(this._inProgress.field) &&
      isMultiSelectOperator(this._inProgress.operator)
    ) {
      if (this._inProgress.promptMultiValues === undefined && this._inProgress.multiValues) {
        return this._inProgress.multiValues;
      }
      return (this._inProgress.promptMultiValues ?? []).map(getPromptSelectionKey);
    }

    if (
      this.isTraceVersionedEntityField(this._inProgress.field) &&
      isMultiSelectOperator(this._inProgress.operator)
    ) {
      return (this._inProgress.entityMultiValues ?? []).map(getVersionedEntitySelectionKey);
    }

    return this._inProgress.multiValues ?? [];
  }

  @computed get multiValueDisplay(): string[] {
    const displayValues = this._inProgress.multiValueDisplay;
    if (!displayValues || displayValues.length === 0) {
      return this.multiValues;
    }
    return this.multiValues.map((value, index) => displayValues[index] ?? value);
  }

  @computed get operatorSuggestions() {
    return matchOperatorsForFilter(this._schema, this._inProgress.field, this._inputText);
  }

  @action setInputText(text: string) {
    this._inputText = text;
    this._selectedFilterIdForRemoval = null;
  }

  @action setStep(step: BuilderStep) {
    this._step = step;
  }

  @action setInProgress(partial: Partial<InProgressToken>) {
    this._inProgress = { ...this._inProgress, ...partial };
  }

  @action setKeyboardIndex(index: number) {
    this._keyboardIndex = index;
  }

  @action setDropdownOpen(open: boolean) {
    this._dropdownOpen = open;
  }

  @action setEditingFilterId(id: string | null) {
    this._editingFilterId = id;
  }

  @action setSelectedFilterIdForRemoval(id: string | null) {
    this._selectedFilterIdForRemoval = id;
  }

  @action setDateRange(range: DateRange | undefined) {
    const normalizedRange = normalizeStructuredSearchDateRange(range, this._dateRange);
    if (areDateRangesEqual(this._dateRange, normalizedRange)) {
      return;
    }

    this._dateRange = normalizedRange;
  }

  @action setSearch(query: string) {
    this._q = query;
    this.resetToFirstPage();
  }

  @action setPageIndex(pageIndex: number) {
    this._pageIndex = Math.max(1, pageIndex);
  }

  @action setPageSize(pageSize: number) {
    this._pageSize = pageSize;
  }

  @action setSorting(sorting: StructuredSearchSorting[]) {
    this._sorting = sorting;
  }

  @action resetToFirstPage() {
    this._pageIndex = 1;
  }

  @action openFieldSuggest() {
    this._step = "field";
    this._inProgress = {};
    this._dropdownOpen = true;
    this._keyboardIndex = 0;
    this._selectedFilterIdForRemoval = null;
  }

  @action selectField(field: string, type: FilterValueType) {
    if (this.isPromptField(field) && this.hasLockedPromptFilter) {
      return;
    }
    this._inProgress = createFieldSelectionState(field, type);
    this._step = this.isNestedField(field) ? "key" : "operator";
    this._inputText = "";
    this._dropdownOpen = true;
    this._keyboardIndex = 0;
    this._selectedFilterIdForRemoval = null;
  }

  @action selectMetadataKey(key: string, keyDisplay?: string) {
    this._inProgress = {
      ...this._inProgress,
      nestedKey: key,
      nestedKeyDisplay: keyDisplay,
    };
    this._step = "operator";
    this._inputText = "";
    this._dropdownOpen = true;
    this._keyboardIndex = 0;
    this._selectedFilterIdForRemoval = null;
  }

  @action editNestedKey() {
    this._inProgress = this.createBacktrackedKeySelectionState(this._inProgress);
    this._step = "key";
    this._inputText = "";
    this._dropdownOpen = true;
    this._keyboardIndex = 0;
    this._selectedFilterIdForRemoval = null;
  }

  @action selectOperator(operator: string) {
    this._inProgress = this.createOperatorSelectionState(this._inProgress, operator);
    const ops = getOperatorsForFilter(this._schema, this._inProgress.field);
    const opDef = ops.find(o => o.operator === operator);
    this._inputText = "";
    this._selectedFilterIdForRemoval = null;
    if (opDef?.valueInput === "none") {
      this.completeFilter();
    } else {
      this._step = "value";
      this._dropdownOpen = !this.shouldSuppressValueDropdown(
        this._inProgress.field,
        this._inProgress.operator,
      );
    }
    this._keyboardIndex = 0;
  }

  @action setValue(value: FilterValue, valueDisplay?: string) {
    const nextInProgress: InProgressToken = { ...this._inProgress, value };
    if (valueDisplay !== undefined) {
      nextInProgress.valueDisplay = valueDisplay;
    } else {
      delete nextInProgress.valueDisplay;
    }
    this._inProgress = nextInProgress;
  }

  @action selectPrompt(promptId: number, promptName: string) {
    this._inProgress = {
      ...this._inProgress,
      promptId,
      promptName,
    };
    this._inputText = "";
    this._keyboardIndex = 0;
    this._dropdownOpen = true;
  }

  @action selectVersionedEntity(entityId: number, entityName: string) {
    this._inProgress = {
      ...this._inProgress,
      entityId,
      entityName,
    };
    this._inputText = "";
    this._keyboardIndex = 0;
    this._dropdownOpen = true;
  }

  @action addPromptMultiValue(promptValue: PromptFilterValue, displayValue: string) {
    const current = this._inProgress.promptMultiValues ?? [];
    const currentDisplay = this._inProgress.multiValueDisplay ?? [];
    const selectionKey = getPromptSelectionKey(promptValue);
    const alreadySelected = current.some(value => getPromptSelectionKey(value) === selectionKey);

    this._inProgress = {
      ...this._inProgress,
      promptId: undefined,
      promptName: undefined,
      valueDisplay: undefined,
      promptMultiValues: alreadySelected ? current : [...current, promptValue],
      multiValueDisplay: alreadySelected ? currentDisplay : [...currentDisplay, displayValue],
    };
    this._inputText = "";
    this._dropdownOpen = true;
  }

  @action selectPromptAnyVersion() {
    if (!this._inProgress.promptId) {
      return;
    }

    const promptName = this._inProgress.promptName ?? "";
    const displayLabel = promptName
      ? `${promptName} (${PROMPT_ANY_VERSION_LABEL})`
      : PROMPT_ANY_VERSION_LABEL;

    if (isMultiSelectOperator(this._inProgress.operator)) {
      this.addPromptMultiValue(
        {
          prompt_id: this._inProgress.promptId,
        },
        displayLabel,
      );
      return;
    }

    this.setValue(JSON.stringify({ prompt_id: this._inProgress.promptId }), displayLabel);
    this.completeFilter();
  }

  @action addVersionedEntityMultiValue(
    entityValue: VersionedEntityFilterValue,
    displayValue: string,
  ) {
    const current = this._inProgress.entityMultiValues ?? [];
    const currentDisplay = this._inProgress.multiValueDisplay ?? [];
    const selectionKey = getVersionedEntitySelectionKey(entityValue);
    const alreadySelected = current.some(
      value => getVersionedEntitySelectionKey(value) === selectionKey,
    );

    this._inProgress = {
      ...this._inProgress,
      entityId: undefined,
      entityName: undefined,
      valueDisplay: undefined,
      entityMultiValues: alreadySelected ? current : [...current, entityValue],
      multiValueDisplay: alreadySelected ? currentDisplay : [...currentDisplay, displayValue],
    };
    this._inputText = "";
    this._dropdownOpen = true;
  }

  @action selectVersionedEntityAnyVersion() {
    const { entityId } = this._inProgress;
    if (entityId === undefined) {
      return;
    }

    const config = getVersionedEntityConfig(this._schema, this._inProgress.field);
    if (!config) {
      return;
    }

    const entityName = this._inProgress.entityName ?? "";
    const displayLabel = formatVersionedEntityDisplayLabel(config.kind, entityName);

    if (isMultiSelectOperator(this._inProgress.operator)) {
      this.addVersionedEntityMultiValue(
        createVersionedEntityFilterValue(config.kind, entityId),
        displayLabel,
      );
      return;
    }

    this.setValue(
      JSON.stringify(createVersionedEntityFilterValue(config.kind, entityId)),
      displayLabel,
    );
    this.completeFilter();
  }

  @action addMultiValue(value: string, displayValue?: string) {
    const current = this._inProgress.multiValues ?? [];
    const currentDisplay = this._inProgress.multiValueDisplay ?? [];
    const nextValues = normalizeMultiValueEntries([value]).filter(item => !current.includes(item));
    if (nextValues.length > 0) {
      const nextDisplay = nextValues.map(item =>
        displayValue && item === value ? displayValue : item,
      );
      this._inProgress = {
        ...this._inProgress,
        multiValues: [...current, ...nextValues],
        multiValueDisplay: [...currentDisplay, ...nextDisplay],
        valueDisplay: undefined,
      };
    }
    this._inputText = "";
    this._dropdownOpen = true;
  }

  @action removeMultiValue(value: string) {
    if (
      this.isPromptField(this._inProgress.field) &&
      isMultiSelectOperator(this._inProgress.operator)
    ) {
      if (this._inProgress.promptMultiValues === undefined && this._inProgress.multiValues) {
        const current = this._inProgress.multiValues;
        const currentDisplay = this._inProgress.multiValueDisplay ?? [];
        const valueIndex = current.indexOf(value);
        this._inProgress = {
          ...this._inProgress,
          multiValues: current.filter(item => item !== value),
          multiValueDisplay:
            valueIndex === -1
              ? currentDisplay
              : currentDisplay.filter((_, index) => index !== valueIndex),
          valueDisplay: undefined,
        };
        return;
      }

      const promptValues = this._inProgress.promptMultiValues ?? [];
      const promptIndex = promptValues.findIndex(item => getPromptSelectionKey(item) === value);
      this._inProgress = {
        ...this._inProgress,
        promptMultiValues: promptValues.filter(item => getPromptSelectionKey(item) !== value),
        multiValueDisplay:
          promptIndex === -1
            ? this._inProgress.multiValueDisplay
            : (this._inProgress.multiValueDisplay ?? []).filter(
                (_, index) => index !== promptIndex,
              ),
        valueDisplay: undefined,
      };
      return;
    }

    if (
      this.isTraceVersionedEntityField(this._inProgress.field) &&
      isMultiSelectOperator(this._inProgress.operator)
    ) {
      const entityValues = this._inProgress.entityMultiValues ?? [];
      const entityIndex = entityValues.findIndex(
        item => getVersionedEntitySelectionKey(item) === value,
      );
      this._inProgress = {
        ...this._inProgress,
        entityMultiValues: entityValues.filter(
          item => getVersionedEntitySelectionKey(item) !== value,
        ),
        multiValueDisplay:
          entityIndex === -1
            ? this._inProgress.multiValueDisplay
            : (this._inProgress.multiValueDisplay ?? []).filter(
                (_, index) => index !== entityIndex,
              ),
        valueDisplay: undefined,
      };
      return;
    }

    const current = this._inProgress.multiValues ?? [];
    const currentDisplay = this._inProgress.multiValueDisplay ?? [];
    const valueIndex = current.indexOf(value);
    this._inProgress = {
      ...this._inProgress,
      multiValues: current.filter(v => v !== value),
      multiValueDisplay:
        valueIndex === -1
          ? currentDisplay
          : currentDisplay.filter((_, index) => index !== valueIndex),
      valueDisplay: undefined,
    };
  }

  @action setNestedKey(key: string, keyDisplay?: string) {
    this._inProgress = {
      ...this._inProgress,
      nestedKey: key,
      nestedKeyDisplay: keyDisplay,
    };
  }

  @action backspaceRemoveFilter(targetId?: string): boolean {
    const removableFilters = this._filters.filter(filter => !this.isFilterLocked(filter.id));
    const hasPlainSearch = Boolean(this._q.trim());
    const fallbackId =
      removableFilters[removableFilters.length - 1]?.id ??
      (hasPlainSearch ? PLAIN_SEARCH_CHIP_ID : undefined);
    const effectiveTargetId = targetId ?? fallbackId;
    if (!effectiveTargetId) {
      return false;
    }
    if (effectiveTargetId !== PLAIN_SEARCH_CHIP_ID && this.isFilterLocked(effectiveTargetId)) {
      return false;
    }

    if (this._selectedFilterIdForRemoval === effectiveTargetId) {
      if (effectiveTargetId === PLAIN_SEARCH_CHIP_ID) {
        this._q = "";
        this._selectedFilterIdForRemoval = null;
        return true;
      }

      this.removeFilter(effectiveTargetId);
      const remainingFilters = removableFilters.filter(filter => filter.id !== effectiveTargetId);
      const newLast = remainingFilters[remainingFilters.length - 1];
      this._selectedFilterIdForRemoval =
        newLast?.id ?? (hasPlainSearch ? PLAIN_SEARCH_CHIP_ID : null);
      return true;
    }

    this._selectedFilterIdForRemoval = effectiveTargetId;
    return true;
  }

  @action backspaceBuilderStep(): boolean {
    this._applyValidationMessage = null;
    this._selectedFilterIdForRemoval = null;

    switch (this._step) {
      case "value": {
        if (this.getVersionedEntityStepMode() === VersionedEntityStepMode.SelectVersion) {
          if (this.isPromptField(this._inProgress.field)) {
            this._inProgress = {
              ...this._inProgress,
              promptId: undefined,
              promptName: undefined,
            };
          } else {
            this._inProgress = {
              ...this._inProgress,
              entityId: undefined,
              entityName: undefined,
            };
          }
          this._inputText = "";
          this._dropdownOpen = true;
          this._keyboardIndex = 0;
          return true;
        }

        if (!this._inProgress.operator) {
          return false;
        }

        const operatorSuggestions = getOperatorsForFilter(this._schema, this._inProgress.field);
        this._inProgress = this.createBacktrackedOperatorSelectionState(this._inProgress);
        this._step = "operator";
        this._inputText = "";
        this._dropdownOpen = true;
        this._keyboardIndex = Math.max(
          0,
          operatorSuggestions.findIndex(
            operator => operator.operator === this._inProgress.operator,
          ),
        );
        return true;
      }

      case "operator":
        if (this.isNestedField(this._inProgress.field) && this._inProgress.nestedKey) {
          this._inProgress = this.createBacktrackedKeySelectionState(this._inProgress);
          this._step = "key";
          this._inputText = "";
          this._dropdownOpen = true;
          this._keyboardIndex = 0;
          return true;
        }

        this._inProgress = {};
        this._step = "field";
        this._inputText = "";
        this._dropdownOpen = true;
        this._keyboardIndex = 0;
        return true;

      case "key":
        this._inProgress = {};
        this._step = "field";
        this._inputText = "";
        this._dropdownOpen = true;
        this._keyboardIndex = 0;
        return true;

      case "field":
        this.cancelBuilder();
        return true;

      default:
        return false;
    }
  }

  @action completeFilter(): boolean {
    this._applyValidationMessage = null;
    const { field, type, operator } = this._inProgress;
    if (!field || !type || !operator) {
      return true;
    }

    const ops = getOperatorsForFilter(this._schema, field);
    const opDef = ops.find(o => o.operator === operator);
    const needsValue = opDef?.valueInput && opDef.valueInput !== "none";
    let value = needsValue ? this._inProgress.value : true;
    if (isMultiSelectOperator(operator)) {
      if (this.isPromptField(field)) {
        const promptMulti = this._inProgress.promptMultiValues ?? [];
        if (promptMulti.length > 0) {
          value = promptMulti;
        } else if (typeof value === "string") {
          const parsed = parseCommaSeparatedValues(value);
          if (parsed.length === 0) {
            this._applyValidationMessage = "Add at least one prompt";
            return false;
          }
          value = parsed;
          this._inProgress = {
            ...this._inProgress,
            multiValueDisplay: parsed,
          };
        } else {
          this._applyValidationMessage = "Select at least one prompt";
          return false;
        }
      } else if (this.isTraceVersionedEntityField(field)) {
        const entityMulti = this._inProgress.entityMultiValues ?? [];
        if (entityMulti.length > 0) {
          value = entityMulti;
        } else {
          this._applyValidationMessage = "Select at least one value";
          return false;
        }
      } else if (typeof value === "string") {
        const multi = this._inProgress.multiValues ?? [];
        if (multi.length > 0) {
          value = multi;
        } else {
          const parsed = parseCommaSeparatedValues(value);
          if (parsed.length === 0) {
            this._applyValidationMessage = "Add at least one value";
            return false;
          }
          value = parsed;
        }
      } else if (
        Array.isArray(this._inProgress.multiValues) &&
        this._inProgress.multiValues.length > 0
      ) {
        value = this._inProgress.multiValues;
      } else {
        this._applyValidationMessage = "Add at least one value";
        return false;
      }
    }

    if (
      isMultiSelectOperator(operator) &&
      this.isPromptField(field) &&
      Array.isArray(value) &&
      value.some(item => !isPromptFilterValue(item) && !isPromptNameValue(item))
    ) {
      this._applyValidationMessage = "Select at least one prompt";
      return false;
    }

    if (
      isMultiSelectOperator(operator) &&
      this.isTraceVersionedEntityField(field) &&
      Array.isArray(value) &&
      value.some(item => !isVersionedEntityFilterValue(item))
    ) {
      this._applyValidationMessage = "Select at least one value";
      return false;
    }

    if (
      isMultiSelectOperator(operator) &&
      !this.isPromptField(field) &&
      !this.isTraceVersionedEntityField(field) &&
      typeof value === "string"
    ) {
      const parsed = parseCommaSeparatedValues(value);
      if (parsed.length === 0) {
        this._applyValidationMessage = "Add at least one value";
        return false;
      }
      value = parsed;
    }

    const needsDateValue = opDef?.valueInput === "date" || opDef?.valueInput === "daterange";

    const rawInput = String((this._inputText || this._inProgress.value) ?? "").trim();
    let valueDisplay = isMultiSelectOperator(operator) ? undefined : this._inProgress.valueDisplay;

    const coerced = coerceCompletionValue(this._schema, {
      field,
      operator,
      rawInput,
      value,
    });
    if (coerced) {
      if ("error" in coerced) {
        this._applyValidationMessage = coerced.error;
        return false;
      }
      ({ value, valueDisplay } = coerced);
    } else if (type === FilterValueType.Number && operator === "between") {
      const parsedBetween = parseNumericBetweenValue(value);
      if (!parsedBetween) {
        this._applyValidationMessage = "Enter a valid number range (e.g. 1, 100)";
        return false;
      }
      value = parsedBetween;
    }

    if (needsValue && (value === undefined || value === "")) {
      if (needsDateValue) {
        this._applyValidationMessage = "Select a date and click Apply";
      } else {
        this._applyValidationMessage = "Enter a value";
      }
      return false;
    }

    const filterId = this._editingFilterId ?? crypto.randomUUID();
    const filter: Filter = {
      id: filterId,
      field,
      type,
      operator,
      value: value as FilterValue,
      valueDisplay,
      multiValueDisplay: isMultiSelectOperator(operator)
        ? this._inProgress.multiValueDisplay
        : undefined,
      nestedKey: this._inProgress.nestedKey,
      nestedKeyDisplay: this._inProgress.nestedKeyDisplay,
      icon: getFilterDefinitionByKey(this._schema, field)?.icon,
      iconClassName: getFilterDefinitionByKey(this._schema, field)?.iconClassName,
    };

    if (this._editingFilterId) {
      this._filters = this._filters.map(item =>
        item.id === this._editingFilterId ? filter : item,
      );
    } else {
      this._filters = [...this._filters, filter];
    }

    this._inProgress = {};
    this._step = "idle";
    this._inputText = "";
    this._dropdownOpen = false;
    this._editingFilterId = null;
    this._selectedFilterIdForRemoval = null;
    return true;
  }

  @action clearApplyValidationMessage() {
    this._applyValidationMessage = null;
  }

  @action removeFilter(id: string) {
    if (this.isFilterLocked(id)) {
      return;
    }
    this._filters = this._filters.filter(filter => filter.id !== id);
    if (this._editingFilterId === id) {
      this._editingFilterId = null;
      this._inProgress = {};
      this._step = "idle";
      this._inputText = "";
      this._dropdownOpen = false;
    }
    this._selectedFilterIdForRemoval = null;
  }

  getMultiValueFilterValues(field: string): string[] {
    const filter = this._filters.find(
      item => !this.isFilterLocked(item.id) && item.field === field && item.operator === "in",
    );
    return filter ? getStringFilterValues(filter) : [];
  }

  @action toggleMultiValueFilter({
    field,
    type,
    value,
    valueDisplay,
  }: ToggleMultiValueFilterOptions) {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return;
    }

    const existingFilter = this._filters.find(
      item =>
        !this.isFilterLocked(item.id) &&
        item.field === field &&
        (item.operator === "in" || item.operator === "is"),
    );

    if (!existingFilter) {
      this._filters = [
        ...this._filters,
        {
          id: crypto.randomUUID(),
          field,
          type,
          operator: "in",
          value: [normalizedValue],
          multiValueDisplay: [valueDisplay ?? normalizedValue],
        },
      ];
      return;
    }

    const currentValues = getStringFilterValues(existingFilter);
    const currentDisplayValues =
      existingFilter.multiValueDisplay && existingFilter.multiValueDisplay.length > 0
        ? existingFilter.multiValueDisplay
        : currentValues;
    const existingValueIndex = currentValues.indexOf(normalizedValue);
    const nextValues =
      existingValueIndex === -1
        ? [...currentValues, normalizedValue]
        : currentValues.filter(item => item !== normalizedValue);

    if (nextValues.length === 0) {
      this._filters = this._filters.filter(item => item.id !== existingFilter.id);
      return;
    }

    const nextDisplayValues =
      existingValueIndex === -1
        ? [...currentDisplayValues, valueDisplay ?? normalizedValue]
        : currentDisplayValues.filter((_, index) => index !== existingValueIndex);

    this._filters = this._filters.map(item =>
      item.id === existingFilter.id
        ? {
            ...item,
            operator: "in",
            value: nextValues,
            multiValueDisplay: nextDisplayValues,
            valueDisplay: undefined,
          }
        : item,
    );
  }

  @action setMultiValueFilter({ field, type, values }: SetMultiValueFilterOptions) {
    const normalizedValues = values
      .map(({ value, valueDisplay }) => ({
        value: value.trim(),
        valueDisplay: valueDisplay?.trim(),
      }))
      .filter(({ value }) => value.length > 0);
    const existingFilter = this._filters.find(
      item => !this.isFilterLocked(item.id) && item.field === field,
    );

    if (normalizedValues.length === 0) {
      if (existingFilter) {
        this._filters = this._filters.filter(
          item => this.isFilterLocked(item.id) || item.field !== field,
        );
      }
      return;
    }

    const nextValues = normalizedValues.map(({ value }) => value);
    const nextDisplayValues = normalizedValues.map(
      ({ value, valueDisplay }) => valueDisplay ?? value,
    );

    if (!existingFilter) {
      this._filters = [
        ...this._filters,
        {
          id: crypto.randomUUID(),
          field,
          type,
          operator: "in",
          value: nextValues,
          multiValueDisplay: nextDisplayValues,
        },
      ];
      return;
    }

    this._filters = this._filters.flatMap(item => {
      if (item.id === existingFilter.id) {
        return [
          {
            ...item,
            type,
            operator: "in",
            value: nextValues,
            multiValueDisplay: nextDisplayValues,
            valueDisplay: undefined,
          },
        ];
      }

      return !this.isFilterLocked(item.id) && item.field === field ? [] : [item];
    });
  }

  @action startEditFilter(id: string) {
    if (this.isFilterLocked(id)) {
      return;
    }
    const filter = this._filters.find(f => f.id === id);
    if (!filter) {
      return;
    }
    this._selectedFilterIdForRemoval = null;
    this._editingFilterId = id;
    const multiValues =
      isMultiSelectOperator(filter.operator) &&
      (!this.isPromptField(filter.field) ||
        (Array.isArray(filter.value) && filter.value.every(isPromptNameValue)))
        ? getNormalizedMultiValues(filter.value)
        : undefined;
    const promptMultiValues =
      this.isPromptField(filter.field) &&
      isMultiSelectOperator(filter.operator) &&
      isPromptFilterValueList(filter.value)
        ? filter.value
        : undefined;
    const entityMultiValues =
      this.isTraceVersionedEntityField(filter.field) &&
      isMultiSelectOperator(filter.operator) &&
      isVersionedEntityFilterValueList(filter.value)
        ? filter.value
        : undefined;
    this._inProgress = {
      field: filter.field,
      type: filter.type,
      operator: filter.operator,
      value: filter.value,
      valueDisplay: filter.valueDisplay,
      multiValueDisplay: filter.multiValueDisplay,
      nestedKey: filter.nestedKey,
      nestedKeyDisplay: filter.nestedKeyDisplay,
      multiValues,
      promptMultiValues,
      entityMultiValues,
    };
    const ops = getOperatorsForFilter(this._schema, filter.field);
    const opDef = ops.find(o => o.operator === filter.operator);
    const needsValue = Boolean(opDef?.valueInput && opDef.valueInput !== "none");
    const step: BuilderStep = needsValue ? "value" : "operator";
    this._inputText = this.getInitialEditInputText(filter, needsValue, opDef?.display);
    this._step = step;
    this._dropdownOpen =
      step !== "value" ||
      !this.shouldSuppressValueDropdown(this._inProgress.field, this._inProgress.operator);
    this._keyboardIndex =
      step === "operator"
        ? Math.max(
            0,
            ops.findIndex(o => o.operator === filter.operator),
          )
        : 0;
  }

  @action cancelEdit() {
    this._editingFilterId = null;
    this._inProgress = {};
    this._step = "idle";
    this._inputText = "";
    this._dropdownOpen = false;
    this._selectedFilterIdForRemoval = null;
    this._applyValidationMessage = null;
  }

  @action replaceSchema(schema: StructuredSearchSchema) {
    if (this._schema.id === schema.id) {
      return;
    }

    this._schemaStateById.set(this._schema.id, {
      filters: this._filters,
      q: this._q,
      dateRange: cloneCompleteDateRange(this._dateRange),
      sorting: [...this._sorting],
      pageIndex: this._pageIndex,
      pageSize: this._pageSize,
    });

    this.cancelBuilder();
    this._editingFilterId = null;
    this._schema = schema;

    const restored = this._schemaStateById.get(schema.id);
    if (restored) {
      this._filters = restored.filters;
      this._q = restored.q;
      this._dateRange = restored.dateRange;
      this._sorting = restored.sorting;
      this._pageIndex = restored.pageIndex;
      this._pageSize = restored.pageSize;
    } else {
      this._filters = [...this._lockedFilters];
      this._q = "";
      this._dateRange = cloneCompleteDateRange(this._createDefaultDateRange());
      this._sorting = [];
      this._pageIndex = 1;
    }
  }

  @action cancelBuilder() {
    this._inProgress = {};
    this._step = "idle";
    this._inputText = "";
    this._dropdownOpen = false;
    this._selectedFilterIdForRemoval = null;
    this._applyValidationMessage = null;
  }

  @action reset() {
    this._inputText = "";
    this._step = "idle";
    this._inProgress = {};
    this._filters = [...this._lockedFilters];
    this._dateRange = cloneCompleteDateRange(this._createDefaultDateRange());
    this._q = "";
    this._pageIndex = 1;
    this._sorting = [];
    this._keyboardIndex = 0;
    this._dropdownOpen = false;
    this._editingFilterId = null;
    this._selectedFilterIdForRemoval = null;
  }

  @action setFiltersFromURL(filters: Filter[]) {
    const lockedIdentities = new Set(this._lockedFilters.map(getFilterIdentity));
    const urlFilters = filters.filter(filter => !lockedIdentities.has(getFilterIdentity(filter)));
    this._filters = [...this._lockedFilters, ...urlFilters];
  }

  @action setLockedFilters(filters: Filter[]) {
    const lockedFilters = filters.map((filter, index) => ({
      ...filter,
      id: filter.id.startsWith(LOCKED_FILTER_ID_PREFIX)
        ? filter.id
        : `${LOCKED_FILTER_ID_PREFIX}${filter.id || index}`,
    }));
    const lockedIdentities = new Set(lockedFilters.map(getFilterIdentity));
    const editableFilters = this._filters.filter(
      filter => !this.isFilterLocked(filter.id) && !lockedIdentities.has(getFilterIdentity(filter)),
    );
    this._lockedFilters = lockedFilters;
    this._filters = [...lockedFilters, ...editableFilters];
  }

  isFilterLocked(id: string) {
    return this._lockedFilters.some(filter => filter.id === id);
  }

  @action addPromptFilter(promptId: number, promptVersionNumber?: number, promptName?: string) {
    const promptFilter = getPromptFilterDefinition(this._schema);
    const promptFieldName = promptFilter?.key;
    if (!promptFieldName || !promptFilter) {
      return;
    }

    const promptAlreadySelected = this._filters.some(
      filter =>
        filter.field === promptFieldName &&
        (filter.operator === "is" || filter.operator === "in") &&
        promptFilterValueMatchesPrompt(filter.value, promptId),
    );

    if (promptAlreadySelected) {
      return;
    }

    const value: FilterValue =
      promptVersionNumber !== undefined
        ? { prompt_id: promptId, prompt_version_number: promptVersionNumber }
        : { prompt_id: promptId };
    let valueDisplay: string | undefined;
    if (promptName && promptVersionNumber !== undefined) {
      valueDisplay = `${promptName} [#${promptVersionNumber}]`;
    } else if (promptName) {
      valueDisplay = `${promptName} (${PROMPT_ANY_VERSION_LABEL})`;
    } else {
      valueDisplay = undefined;
    }
    const filter: Filter = {
      id: crypto.randomUUID(),
      field: promptFieldName,
      type: FilterValueType.Prompt,
      operator: PROMPT_FILTER_OPERATOR,
      value,
      valueDisplay,
      icon: promptFilter.icon,
      iconClassName: promptFilter.iconClassName,
    };
    this._filters = [...this._filters, filter];
  }
}
